"use client";

import { useState } from "react";
import {
  CopyButton,
  Notice,
  Panel,
  TextField,
} from "@/components/tool-kit/primitives";
import { HINT, LABEL, WIDGET_H2 } from "@/components/tool-kit/styles";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  cssBlock,
  type FormatKey,
  formatRows,
  nearestName,
} from "@/lib/tool-kit/color-charts/color-output";
import { parseColor } from "@/lib/tool-kit/color-charts/color-parse";
import {
  type Rgb,
  toHex,
  toRgbString,
} from "@/lib/tool-kit/color-charts/color-space";

/** English strings. A translated content file overrides these through `ui`. */
export const COLOR_CONVERTER_STRINGS = {
  inputLabel: "Color — HEX, rgb(), hsl(), hwb(), hsv(), cmyk() or a CSS name",
  inputHint:
    "Examples: #336699, rgb(51 102 153), hsl(210 50% 40%), hwb(210 20% 40%), cmyk(67%, 33%, 0%, 40%), rebeccapurple. Add an alpha with #rrggbbaa or “/ 50%”.",
  inputPlaceholder: "#336699",
  pickerLabel: "Or pick a color",
  previewLabel: "Preview of {color}",
  results: "Converted values",
  formatHex: "HEX",
  formatHex8: "HEX with alpha",
  formatRgb: "RGB",
  formatHsl: "HSL",
  formatHwb: "HWB",
  formatHsv: "HSV",
  formatCmyk: "CMYK (naive)",
  formatOklch: "OKLCH",
  cssName: "CSS name",
  nameExact: "{name}",
  nameNearest: "{name} (nearest)",
  alphaLabel: "Alpha",
  alphaValue: "{percent}% opaque",
  copyFormat: "Copy {format}",
  copied: "Copied!",
  cssHeading: "CSS custom properties",
  copyCss: "Copy CSS",
  cmykNote:
    "CMYK here is the simple device conversion from RGB — no inks, paper or ICC profile are involved — so it will not match a printed color. Ask your printer for their profile before you send artwork.",
  hsvNote:
    "HSV and CMYK are not CSS color functions; the page shows them for design and print tools.",
  invalid:
    "That is not a color this tool can read. Try #336699, rgb(51 102 153), hsl(210 50% 40%) or a name such as rebeccapurple.",
} as const;

type Strings = typeof COLOR_CONVERTER_STRINGS;

const FORMAT_LABEL: Record<FormatKey, keyof Strings> = {
  hex: "formatHex",
  hex8: "formatHex8",
  rgb: "formatRgb",
  hsl: "formatHsl",
  hwb: "formatHwb",
  hsv: "formatHsv",
  cmyk: "formatCmyk",
  oklch: "formatOklch",
};

const DEFAULT_COLOR = "#336699";

function Preview({ color, label }: { color: Rgb; label: string }) {
  return (
    <div
      className="h-24 overflow-hidden rounded-lg border border-border"
      style={{
        backgroundImage:
          "linear-gradient(45deg,#d4d4d4 25%,transparent 25%,transparent 75%,#d4d4d4 75%),linear-gradient(45deg,#d4d4d4 25%,#fff 25%,#fff 75%,#d4d4d4 75%)",
        backgroundPosition: "0 0,8px 8px",
        backgroundSize: "16px 16px",
      }}
    >
      <div
        aria-label={label}
        className="h-full w-full"
        role="img"
        style={{ backgroundColor: toRgbString(color) }}
      />
    </div>
  );
}

function Results({ color, strings }: { color: Rgb; strings: Strings }) {
  const fmt = useToolNumber();
  const name = nearestName(color);
  const css = cssBlock(color);
  return (
    <div aria-live="polite" className="space-y-4">
      <h2 className={WIDGET_H2}>{strings.results}</h2>
      <ul className="divide-y divide-border rounded-lg border border-border bg-card">
        {formatRows(color).map((row) => {
          const label = strings[FORMAT_LABEL[row.key]];
          return (
            <li
              className="flex flex-wrap items-center justify-between gap-3 px-4 py-3"
              key={row.key}
            >
              <div className="min-w-0">
                <p className={HINT}>{label}</p>
                <p className="break-all font-mono text-foreground text-sm">
                  {row.value}
                </p>
              </div>
              <CopyButton
                copiedLabel={strings.copied}
                label={t(strings.copyFormat, { format: label })}
                small
                text={row.value}
              />
            </li>
          );
        })}
        <li className="px-4 py-3">
          <p className={HINT}>{strings.cssName}</p>
          <p className="font-mono text-foreground text-sm">
            {t(name.exact ? strings.nameExact : strings.nameNearest, {
              name: name.name,
            })}
          </p>
        </li>
        {color.a < 1 ? (
          <li className="px-4 py-3">
            <p className={HINT}>{strings.alphaLabel}</p>
            <p className="text-foreground text-sm">
              {t(strings.alphaValue, {
                percent: fmt(Math.round(color.a * 1000) / 10),
              })}
            </p>
          </li>
        ) : null}
      </ul>
      <Panel title={strings.cssHeading}>
        <pre className="overflow-x-auto font-mono text-foreground text-sm">
          {css}
        </pre>
        <CopyButton
          copiedLabel={strings.copied}
          label={strings.copyCss}
          text={css}
        />
      </Panel>
      <p className={HINT}>{strings.cmykNote}</p>
      <p className={HINT}>{strings.hsvNote}</p>
    </div>
  );
}

export default function ColorConverterWidget() {
  const strings = useToolUi(COLOR_CONVERTER_STRINGS);
  const [text, setText] = useState(DEFAULT_COLOR);
  const color = parseColor(text);

  function pick(hex: string) {
    const picked = parseColor(hex);
    if (!picked) {
      return;
    }
    const alpha = color?.a ?? 1;
    setText(
      alpha < 1
        ? `${hex}${Math.round(alpha * 255)
            .toString(16)
            .padStart(2, "0")}`
        : hex
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-[1fr_auto] sm:items-end">
        <TextField
          hint={strings.inputHint}
          id="cc-color"
          label={strings.inputLabel}
          mono
          onChange={setText}
          placeholder={strings.inputPlaceholder}
          value={text}
        />
        <div className="space-y-2">
          <label className={LABEL} htmlFor="cc-picker">
            {strings.pickerLabel}
          </label>
          <input
            className="block h-10 w-20 cursor-pointer rounded-lg border border-border bg-card"
            id="cc-picker"
            onChange={(event) => pick(event.target.value)}
            type="color"
            value={color ? toHex(color) : DEFAULT_COLOR}
          />
        </div>
      </div>
      {color ? (
        <>
          <Preview
            color={color}
            label={t(strings.previewLabel, { color: toHex(color) })}
          />
          <Results color={color} strings={strings} />
        </>
      ) : (
        <Notice>{strings.invalid}</Notice>
      )}
    </div>
  );
}
